import React, { useEffect, useMemo, useState } from 'react';
import { useToast, useConfirm, usePrompt } from '../context/UIContext';
import InvoiceModal from '../modals/InvoiceModal';
import OrderModal from '../modals/OrderModal';
import BwaDashboard from '../components/BwaDashboard';
import BwaAnalytics from '../components/BwaAnalytics';
import {
    createInvoice, deleteInvoice, downloadInvoicePdf, getAllInvoices, updateInvoice,
    createOffer, deleteOffer, downloadOfferPdf, getAllOffers, updateOffer,
    convertOfferToOrder,
} from '../services/InvoiceService';
import {
    getAllOrders, createOrder, updateOrder, deleteOrder,
    uploadOrderFile, getOrderFile,
} from '../services/OrderService';
import { getRevenueTargets } from '../services/OrganisationService';
import RevenueProgressChart from '../components/RevenueProgressChart';
import { extractApiError } from '../utils/apiError';
import { numberSortValue } from '../utils/documentNumber';
import { offerToOrderDraft } from '../utils/offerToOrder';
import { formatDate } from '../utils/dateConverter';
import { eurAmount, fxDifference, hasFxCorrection } from '../utils/invoiceCurrency';
import { openPendingTab, revealBlobInTab, closePendingTab } from '../utils/pdfTab';
import '../styles/Invoices.css';

const STATUS_LABELS = {
    draft: 'Entwurf', sent: 'Versendet', paid: 'Bezahlt',
    cancelled: 'Storniert', accepted: 'Angenommen', rejected: 'Abgelehnt',
    active: 'Aktiv', completed: 'Abgeschlossen',
};
const INVOICE_STATUSES = ['draft', 'sent', 'paid', 'cancelled'];
const OFFER_STATUSES   = ['draft', 'sent', 'accepted', 'rejected'];
const ORDER_STATUSES   = ['draft', 'active', 'completed', 'cancelled'];
const MONTHS_SHORT     = ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'];
const MONTH_NAMES      = ['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'];
const ACTIVE_STATUSES  = new Set(['sent', 'paid']);
// GET /Invoice caps after ORDER BY IssueDate DESC, so the oldest rows fall off — the very
// year the Umsatzverlauf compares against. 2000 is the endpoint's maximum.
const INVOICE_LIMIT    = 2000;

function fmtDate(d) {
    if (!d) return '—';
    // Not toLocaleDateString: de-DE renders single digits unpadded (20.8.2026).
    return formatDate(d);
}

function fmtEur(n) {
    return `€ ${Number(n || 0).toLocaleString('de-DE', {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    })}`;
}

// Signed EUR, for the FX difference: the sign is the point, so always show it.
function fmtSigned(n) {
    const v = Number(n) || 0;
    return `${v < 0 ? '−' : '+'}${fmtEur(Math.abs(v))}`;
}

function fmtAmount(n, currency = 'EUR') {
    return `${currency} ${Number(n || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
}

// Nullish sorts first; strings compare numeric-aware and case-insensitively.
function compareVals(a, b) {
    if (a == null && b == null) return 0;
    if (a == null) return -1;
    if (b == null) return 1;
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return String(a).localeCompare(String(b), 'de', { numeric: true, sensitivity: 'base' });
}

function nextSort(prev, key) {
    if (prev.key === key) return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
    return { key, dir: 'asc' };
}

function SortableTh({ label, sortKey, sort, onSort, align }) {
    const active = sort.key === sortKey;
    return (
        <th
            className={`th-sortable${active ? ' th-sorted' : ''}`}
            style={align === 'right' ? { textAlign: 'right' } : undefined}
            onClick={() => onSort(sortKey)}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSort(sortKey); } }}
            tabIndex={0}
            aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
        >
            <span className="th-sortable-inner">
                {label}
                <span className="th-arrow">{active ? (sort.dir === 'asc' ? '▲' : '▼') : '⇅'}</span>
            </span>
        </th>
    );
}

// The invoice array arrives unfiltered on purpose: year-scoped vs. all-time is decided here
// (see "Ausstehende Rechnungen"). Exported so it can be tested on props alone.
export function InvoiceOverview({
    invoices, onStatusChange, year, revenueTarget = null, invoicesTruncated = false,
}) {
    const now = new Date();
    const thisYear    = now.getFullYear();
    const thisMonth   = now.getMonth();
    const thisQuarter = Math.floor(thisMonth / 3);
    const shownYear   = year ?? thisYear;
    // Only the running year has a current quarter and month to mark as such; on a
    // past year the month card is a like-for-like comparison, not "today".
    const isThisYear  = shownYear === thisYear;
    const [hoverMonth, setHoverMonth] = useState(null);

    const active      = invoices.filter(inv => ACTIVE_STATUSES.has(inv.status));
    const hasNonEur   = invoices.some(inv => inv.currency && inv.currency !== 'EUR');
    const sumNet   = arr => arr.reduce((s, inv) => s + eurAmount(inv.total_net,   inv), 0);
    const sumGross = arr => arr.reduce((s, inv) => s + eurAmount(inv.total_gross, inv), 0);

    const byYear    = (arr, y)    => arr.filter(inv => inv.issue_date?.startsWith(String(y)));
    const byQuarter = (arr, y, q) => arr.filter(inv => {
        if (!inv.issue_date) return false;
        const d = new Date(inv.issue_date);
        return d.getFullYear() === y && Math.floor(d.getMonth() / 3) === q;
    });
    const byMonth = (arr, y, m) => arr.filter(inv => {
        if (!inv.issue_date) return false;
        const d = new Date(inv.issue_date);
        return d.getFullYear() === y && d.getMonth() === m;
    });

    const yearSlice    = byYear(active, shownYear);
    const monthSlice   = byMonth(active, shownYear, thisMonth);

    // Deliberately not year-scoped: an unpaid invoice from last year is the one that
    // matters most, and a year toggle must not hide the money still being chased.
    const pending       = [...invoices.filter(inv => inv.status === 'sent')]
        .sort((a, b) => (a.due_date || '') < (b.due_date || '') ? -1 : 1);
    const pendingAmount = sumGross(pending);

    const monthlyData = Array.from({ length: 12 }, (_, m) => ({
        month: m,
        net:   sumNet(byMonth(active, shownYear, m)),
        gross: sumGross(byMonth(active, shownYear, m)),
    }));
    const maxNet = Math.max(...monthlyData.map(d => d.net), 1);

    const VB_W = 770, VB_H = 220;
    const PAD_L = 65, PAD_R = 10, PAD_T = 20, PAD_B = 35;
    const cW = VB_W - PAD_L - PAD_R;
    const cH = VB_H - PAD_T - PAD_B;
    const slotW = cW / 12;
    const barW  = slotW * 0.55;
    const barOff = (slotW - barW) / 2;

    const fmtK = v => {
        if (v === 0) return '0';
        if (v >= 10000) return `${(v / 1000).toFixed(0)}k`;
        if (v >= 1000)  return `${(v / 1000).toFixed(1)}k`;
        return Math.round(v).toString();
    };

    const yTicks = [0, 0.25, 0.5, 0.75, 1].map(f => ({
        frac: f, label: fmtK(f * maxNet), y: PAD_T + cH - f * cH,
    }));

    const STATUS_ORDER = ['paid', 'sent', 'draft', 'cancelled'];
    const statusBreakdown = STATUS_ORDER
        .map(s => ({
            status: s,
            count:  invoices.filter(inv => inv.status === s).length,
            net:    sumNet(invoices.filter(inv => inv.status === s)),
            gross:  sumGross(invoices.filter(inv => inv.status === s)),
        }))
        .filter(s => s.count > 0);

    const quarterCards = [1, 2, 3, 4].map(q => {
        const slice = byQuarter(active, shownYear, q - 1);
        return {
            q, net: sumNet(slice), gross: sumGross(slice),
            current: isThisYear && q - 1 === thisQuarter,
        };
    });

    return (
        <div className="inv-overview">

            <div className="inv-kpi-row inv-kpi-row--3">
                <div className="inv-kpi-card">
                    <span className="inv-kpi-label">Umsatz {shownYear}</span>
                    <span className="inv-kpi-value">{fmtEur(sumNet(yearSlice))}</span>
                    <span className="inv-kpi-gross">{fmtEur(sumGross(yearSlice))} brutto</span>
                    <span className="inv-kpi-sub">Versendet &amp; Bezahlt</span>
                </div>
                <div className="inv-kpi-card">
                    <span className="inv-kpi-label">{MONTHS_SHORT[thisMonth]} {shownYear}</span>
                    <span className="inv-kpi-value">{fmtEur(sumNet(monthSlice))}</span>
                    <span className="inv-kpi-gross">{fmtEur(sumGross(monthSlice))} brutto</span>
                    <span className="inv-kpi-sub">{isThisYear ? 'Aktueller Monat' : 'Vergleichsmonat'}</span>
                </div>
                <div className="inv-kpi-card inv-kpi-card--warn">
                    <span className="inv-kpi-label">Ausstehend <span className="inv-card-hint">· alle Jahre</span></span>
                    <span className="inv-kpi-value inv-kpi-value--warn">{fmtEur(sumNet(pending))}</span>
                    <span className="inv-kpi-gross">{fmtEur(pendingAmount)} brutto</span>
                    <span className="inv-kpi-sub">{pending.length} Rechnung{pending.length !== 1 ? 'en' : ''}</span>
                </div>
            </div>

            <div className="inv-kpi-row">
                {quarterCards.map(({ q, net, gross, current }) => (
                    <div key={q} className={`inv-kpi-card${current ? ' inv-kpi-card--current' : ''}`}>
                        <span className="inv-kpi-label">Q{q} / {shownYear}{current ? ' · laufend' : ''}</span>
                        <span className="inv-kpi-value">{fmtEur(net)}</span>
                        <span className="inv-kpi-gross">{fmtEur(gross)} brutto</span>
                        <span className="inv-kpi-sub">Versendet &amp; Bezahlt</span>
                    </div>
                ))}
            </div>

            <div className="inv-chart-card">
                <div className="inv-chart-header">
                    {/* The year comes from the page-wide selector — a second one here
                        could disagree with the KPIs above it. */}
                    <h3 className="inv-chart-title">
                        Monatsumsatz Netto {shownYear} (Versendet &amp; Bezahlt)
                        {hasNonEur && <span className="inv-chart-currency-hint"> · in EUR</span>}
                    </h3>
                </div>
                <svg viewBox={`0 0 ${VB_W} ${VB_H}`} style={{ width: '100%', display: 'block' }}>
                    {yTicks.map(({ frac, label, y }) => (
                        <g key={frac}>
                            <line x1={PAD_L} y1={y} x2={PAD_L + cW} y2={y}
                                style={{ stroke: 'var(--color-border)' }} strokeWidth="1" />
                            <text x={PAD_L - 8} y={y + 4} textAnchor="end" fontSize="10"
                                style={{ fill: 'var(--color-text-muted)' }}>{label}</text>
                        </g>
                    ))}
                    <line x1={PAD_L} y1={PAD_T + cH} x2={PAD_L + cW} y2={PAD_T + cH}
                        style={{ stroke: 'var(--color-border-strong)' }} strokeWidth="1" />
                    {monthlyData.map(({ month, net }) => {
                        const barH = net > 0 ? Math.max(3, (net / maxNet) * cH) : 3;
                        const bx   = PAD_L + month * slotW + barOff;
                        const by   = PAD_T + cH - barH;
                        const cur  = isThisYear && month === thisMonth;
                        const hovered = hoverMonth === month;
                        return (
                            <g key={month}>
                                <rect x={bx} y={by} width={barW} height={barH} rx="3"
                                    style={{ fill: (cur || hovered) ? 'var(--color-primary)' : 'var(--color-primary-light)' }}
                                    opacity={net === 0 ? 0.3 : 1} />
                                {net > 0 && barH > 18 && (
                                    <text x={bx + barW / 2} y={by - 5}
                                        textAnchor="middle" fontSize="9"
                                        style={{ fill: 'var(--color-text-secondary)' }}>
                                        {fmtK(net)}
                                    </text>
                                )}
                                <text x={bx + barW / 2} y={PAD_T + cH + 20}
                                    textAnchor="middle" fontSize="10"
                                    style={{ fill: cur ? 'var(--color-primary)' : 'var(--color-text-secondary)',
                                             fontWeight: cur ? '600' : '400' }}>
                                    {MONTHS_SHORT[month]}
                                </text>
                            </g>
                        );
                    })}

                    {/* Painted last so they stay topmost. A native SVG <title> only shows for the
                        element under the cursor, not for an ancestor <g>. */}
                    {monthlyData.map(({ month }) => (
                        <rect
                            key={`hit-${month}`}
                            x={PAD_L + month * slotW} y={PAD_T}
                            width={slotW} height={cH}
                            fill="transparent"
                            data-testid={`chart-hit-${month}`}
                            onMouseEnter={() => setHoverMonth(month)}
                            onMouseLeave={() => setHoverMonth(null)}
                        />
                    ))}

                    {hoverMonth !== null && (() => {
                        const d = monthlyData[hoverMonth];
                        const TW = 168, TH = 54;
                        const barH = d.net > 0 ? Math.max(3, (d.net / maxNet) * cH) : 3;
                        const barTop = PAD_T + cH - barH;
                        // Clamp inside the plot area, and drop below the bar top when there
                        // is no room above it.
                        const cx = PAD_L + hoverMonth * slotW + slotW / 2;
                        const x = Math.min(Math.max(cx - TW / 2, PAD_L), PAD_L + cW - TW);
                        const y = barTop - TH - 8 >= PAD_T ? barTop - TH - 8 : PAD_T + 4;
                        return (
                            <g style={{ pointerEvents: 'none' }}>
                                <rect x={x} y={y} width={TW} height={TH} rx="4"
                                    style={{ fill: 'var(--color-surface)',
                                             stroke: 'var(--color-border-strong)' }}
                                    strokeWidth="1" />
                                <text x={x + 10} y={y + 16} fontSize="10" fontWeight="700"
                                    style={{ fill: 'var(--color-text)' }}>
                                    {`${MONTH_NAMES[hoverMonth]} ${shownYear}`}
                                </text>
                                <text x={x + 10} y={y + 32} fontSize="10"
                                    style={{ fill: 'var(--color-text-secondary)' }}>
                                    {`Netto ${fmtEur(d.net)}`}
                                </text>
                                <text x={x + 10} y={y + 46} fontSize="10"
                                    style={{ fill: 'var(--color-text-muted)' }}>
                                    {`Brutto ${fmtEur(d.gross)}`}
                                </text>
                            </g>
                        );
                    })()}
                </svg>
            </div>

            <RevenueProgressChart
                invoices={invoices}
                year={shownYear}
                target={revenueTarget}
                truncated={invoicesTruncated}
                hasNonEur={hasNonEur}
            />

            <div className="inv-overview-two-col">

                <div className="inv-card">
                    <h3 className="inv-card-title">
                        Ausstehende Rechnungen
                        <span className="inv-card-hint">· alle Jahre</span>
                        {pending.length > 0 && <span className="inv-card-badge">{pending.length}</span>}
                    </h3>
                    {pending.length === 0 ? (
                        <p className="inv-card-empty">Alle Rechnungen sind bezahlt.</p>
                    ) : (
                        <div className="inv-card-scroll">
                            <table className="invoices-table">
                                <thead>
                                    <tr>
                                        <th>Nr.</th>
                                        <th>Empfänger</th>
                                        <th>Fällig</th>
                                        <th style={{ textAlign: 'right' }}>Netto</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pending.map(inv => {
                                        const overdue = inv.due_date && new Date(inv.due_date) < now;
                                        return (
                                            <tr key={inv.id} className={overdue ? 'inv-row-overdue' : ''}>
                                                <td className="td-number">{inv.invoice_number}</td>
                                                <td>{inv.recipient_name}</td>
                                                <td style={{ color: overdue ? 'var(--color-danger, #ef4444)' : undefined }}>
                                                    {fmtDate(inv.due_date)}{overdue ? ' ⚠' : ''}
                                                </td>
                                                <td className="td-amount">
                                                    {fmtAmount(inv.total_net, inv.currency)}
                                                    {inv.currency !== 'EUR' && <span className="td-eur-hint">{fmtEur(eurAmount(inv.total_net, inv))}</span>}
                                                </td>
                                                <td>
                                                    <button
                                                        className="inv-btn inv-btn--paid"
                                                        onClick={() => onStatusChange(inv.id, 'paid')}
                                                        title="Als bezahlt markieren"
                                                    >
                                                        ✓ Bezahlt
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div className="inv-card">
                    {/* All-time like the outstanding list next to it: the point is how the
                        whole invoice stock is distributed, not one year of it. */}
                    <h3 className="inv-card-title">
                        Status-Übersicht
                        <span className="inv-card-hint">· alle Jahre</span>
                    </h3>
                    {statusBreakdown.length === 0 ? (
                        <p className="inv-card-empty">Noch keine Rechnungen vorhanden.</p>
                    ) : (
                        <div className="inv-status-breakdown">
                            {statusBreakdown.map(({ status, count, net, gross }) => (
                                <div key={status} className="inv-status-row">
                                    <span className={`inv-status ${status}`}>{STATUS_LABELS[status] || status}</span>
                                    <span className="inv-status-count">{count}×</span>
                                    <div className="inv-status-bar-wrap">
                                        <div className="inv-status-bar"
                                            style={{ width: `${(count / invoices.length) * 100}%` }} />
                                    </div>
                                    <div className="inv-status-amounts">
                                        <span className="inv-status-net">{fmtEur(net)}</span>
                                        <span className="inv-status-gross">{fmtEur(gross)} brutto</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
}

// A document belongs to the year of its issue date — ISO dates, so a prefix match.
const inYear = (arr, y) => arr.filter(d => (d.issue_date || '').startsWith(String(y)));

export default function Invoices() {
    const toast = useToast();
    const confirm = useConfirm();
    const prompt = usePrompt();
    const [tab, setTab]           = useState('overview');
    const [bwaTab, setBwaTab]     = useState('analytics');
    // One year for the whole page. BWA is the exception: it brings its own, driven by the
    // months that were actually imported.
    const [year, setYear]         = useState(() => new Date().getFullYear());
    const [invoices, setInvoices] = useState([]);
    // Yearly Umsatzziel, keyed by year, for the Umsatzverlauf chart.
    const [revenueTargets, setRevenueTargets] = useState({});
    const [offers, setOffers]     = useState([]);
    const [orders, setOrders]     = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editDoc, setEditDoc]   = useState(null);
    const [template, setTemplate] = useState(null);
    const [loadingPdf, setLoadingPdf] = useState(null);
    // Separate from loadingPdf: previewing must not grey out the download button.
    const [previewPdf, setPreviewPdf] = useState(null);
    const [showOrderModal, setShowOrderModal] = useState(false);
    const [editOrder, setEditOrder] = useState(null);
    const [loadError, setLoadError] = useState('');
    // Angebot → Auftrag: the offer being converted and the draft the modal opens with.
    const [convertOffer, setConvertOffer] = useState(null);
    const [orderPrefill, setOrderPrefill] = useState(null);

    const [docSearch, setDocSearch]   = useState('');
    const [docMonth, setDocMonth]     = useState('');  // 'YYYY-MM' or '' = all months
    // All three finance lists default the same way: highest document number on top.
    const [docSort, setDocSort]       = useState({ key: 'number', dir: 'desc' });
    const [orderSearch, setOrderSearch] = useState('');
    const [orderSort, setOrderSort]     = useState({ key: 'number', dir: 'desc' });

    // The attached order document, previewed inline on the page (blob URL, because an
    // iframe cannot send the X-API-Key header the API requires).
    const [filePreview, setFilePreview] = useState(null);  // { order, url } | null
    const [fileLoading, setFileLoading] = useState(null);  // order id being fetched

    useEffect(() => { load(); }, []);

    // A blob URL stays alive until it is revoked; drop the previous one whenever the
    // preview changes and the last one when the page goes away.
    useEffect(() => () => { if (filePreview?.url) URL.revokeObjectURL(filePreview.url); },
        [filePreview]);

    const load = () => {
        setLoadError('');
        let cancelled = false;
        Promise.all([getAllInvoices({ limit: INVOICE_LIMIT }), getAllOffers(), getAllOrders()])
            .then(([inv, off, ord]) => { if (!cancelled) { setInvoices(inv); setOffers(off); setOrders(ord); } })
            .catch(() => { if (!cancelled) setLoadError('Daten konnten nicht geladen werden. Bitte versuche es erneut.'); });
        // Separate from the documents: a failure here only means no goal line.
        getRevenueTargets()
            .then(r => { if (!cancelled) setRevenueTargets(r?.targets || {}); })
            .catch(() => { if (!cancelled) setRevenueTargets({}); });
        return () => { cancelled = true; };
    };

    const isOverview = tab === 'overview';
    const isInvoice  = tab === 'invoices';
    const isOrders   = tab === 'orders';

    // Years come from the data, not a fixed window, which would hide everything older. The
    // current and the selected year are always offered.
    const years = useMemo(() => {
        const set = new Set([new Date().getFullYear(), year]);
        [...invoices, ...offers, ...orders].forEach(d => {
            const y = Number((d.issue_date || '').slice(0, 4));
            if (y) set.add(y);
        });
        return Array.from(set).sort((a, b) => b - a);
    }, [invoices, offers, orders, year]);

    const yearInvoices = useMemo(() => inYear(invoices, year), [invoices, year]);
    const yearOffers   = useMemo(() => inYear(offers,   year), [offers,   year]);
    const yearOrders   = useMemo(() => inYear(orders,   year), [orders,   year]);

    // `docs` is what the list shows, `allDocs` what the modal needs: the next number and a
    // customer's last document come from every year, not the one on screen.
    const docs    = isInvoice ? yearInvoices : yearOffers;
    const allDocs = isInvoice ? invoices     : offers;

    // Only the selected year's months, because `docs` is already scoped to it.
    const docMonths = useMemo(() => {
        const set = new Set();
        docs.forEach(d => { if (d.issue_date) set.add(d.issue_date.slice(0, 7)); });
        return Array.from(set).sort().reverse().map(ym => {
            const [y, m] = ym.split('-');
            return { value: ym, label: `${MONTH_NAMES[Number(m) - 1]} ${y}` };
        });
    }, [docs]);

    const visibleDocs = useMemo(() => {
        const q = docSearch.trim().toLowerCase();
        const num = d => (isInvoice ? d.invoice_number : d.offer_number) || '';
        let filtered = docs;
        if (docMonth) {
            filtered = filtered.filter(d => (d.issue_date || '').slice(0, 7) === docMonth);
        }
        if (q) {
            filtered = filtered.filter(d =>
                num(d).toLowerCase().includes(q)
                || (d.recipient_name || '').toLowerCase().includes(q)
                || (d.internal_note || '').toLowerCase().includes(q)
                || (d.rejection_reason || '').toLowerCase().includes(q)
                || (STATUS_LABELS[d.status] || d.status || '').toLowerCase().includes(q));
        }
        const val = d => {
            switch (docSort.key) {
                case 'number':    return numberSortValue(num(d));
                case 'recipient': return d.recipient_name || '';
                case 'comment':   return d.internal_note || '';
                case 'due':       return (isInvoice ? d.due_date : d.valid_until) || '';
                case 'status':    return STATUS_LABELS[d.status] || d.status || '';
                case 'net':       return eurAmount(d.total_net,   d);
                case 'gross':     return eurAmount(d.total_gross, d);
                case 'issue_date':
                default:          return d.issue_date || '';
            }
        };
        const sorted = [...filtered].sort((a, b) => compareVals(val(a), val(b)));
        return docSort.dir === 'desc' ? sorted.reverse() : sorted;
    }, [docs, docSearch, docMonth, docSort, isInvoice]);

    const visibleOrders = useMemo(() => {
        const q = orderSearch.trim().toLowerCase();
        const filtered = q
            ? yearOrders.filter(o =>
                (o.order_number || '').toLowerCase().includes(q)
                || (o.title || '').toLowerCase().includes(q)
                || (o.project_name || '').toLowerCase().includes(q)
                || (o.company_name || '').toLowerCase().includes(q)
                || (STATUS_LABELS[o.status] || o.status || '').toLowerCase().includes(q))
            : yearOrders;
        const val = o => {
            switch (orderSort.key) {
                case 'number':  return numberSortValue(o.order_number);
                case 'title':   return o.title || '';
                case 'project': return o.project_name || '';
                case 'company': return o.company_name || '';
                case 'period':  return o.start_date || o.end_date || '';
                case 'status':  return STATUS_LABELS[o.status] || o.status || '';
                case 'hours':   return Number(o.total_hours) || 0;
                case 'issue_date':
                default:        return o.issue_date || '';
            }
        };
        const sorted = [...filtered].sort((a, b) => compareVals(val(a), val(b)));
        return orderSort.dir === 'desc' ? sorted.reverse() : sorted;
    }, [yearOrders, orderSearch, orderSort]);

    const handleAdd = () => {
        const recent = [...allDocs]
            .filter(d => d.issue_date)
            .sort((a, b) => new Date(b.issue_date) - new Date(a.issue_date))[0] || null;
        setEditDoc(null); setTemplate(recent); setShowModal(true);
    };
    const handleEdit = (doc) => { setEditDoc(doc); setTemplate(null); setShowModal(true); };
    // Duplicating is the template path with a chosen document instead of the latest: the
    // modal opens as a new draft, so nothing is written until the user saves.
    const handleDuplicate = (doc) => { setEditDoc(null); setTemplate(doc); setShowModal(true); };

    const handleDelete = async (doc) => {
        const label = isInvoice ? `Rechnung ${doc.invoice_number}` : `Angebot ${doc.offer_number}`;
        if (!await confirm(`${label} wirklich löschen?`, { confirmLabel: 'Löschen', danger: true })) return;
        try {
            if (isInvoice) await deleteInvoice(doc.id);
            else           await deleteOffer(doc.id);
            load();
        } catch (err) { toast(extractApiError(err, 'Fehler beim Löschen.')); }
    };

    // A new document is dated today even while a past year is selected, so the filter snaps to
    // what was just written instead of dropping the row out of the list.
    const snapYearTo = (issueDate) => {
        const written = Number(String(issueDate || '').slice(0, 4));
        if (!written || written === year) return;
        setYear(written);
        setDocMonth('');   // the stored month belongs to the year we just left
    };

    const handleSave = async (payload) => {
        try {
            if (editDoc) {
                if (isInvoice) await updateInvoice(editDoc.id, payload);
                else           await updateOffer(editDoc.id, payload);
            } else {
                if (isInvoice) await createInvoice(payload);
                else           await createOffer(payload);
            }
            setShowModal(false);
            snapYearTo(payload.issue_date);
            load();
        } catch (err) { toast(extractApiError(err, 'Fehler beim Speichern.')); }
    };

    // Sends { status } alone; UpdateInvoiceDTO is fully optional. A rejection asks for its
    // reason in the same step — a reason collected later is a reason nobody enters.
    const handleQuickStatus = async (docId, newStatus, isInv = true) => {
        let payload = { status: newStatus };
        if (!isInv && newStatus === 'rejected') {
            const doc = offers.find(o => o.id === docId);
            const reason = await prompt(
                `Angebot ${doc?.offer_number || ''} als abgelehnt markieren?`.replace('  ', ' '),
                {
                    confirmLabel: 'Ablehnen',
                    danger: true,
                    placeholder: 'z.B. Preis, Timing, Wettbewerber …',
                    defaultValue: doc?.rejection_reason || '',
                },
            );
            if (reason === null) return;  // cancelled — the select falls back to the stored status
            payload = { status: newStatus, rejection_reason: reason || null };
        }
        try {
            if (isInv) await updateInvoice(docId, payload);
            else       await updateOffer(docId,   payload);
            load();
        } catch (err) { toast(extractApiError(err, 'Status konnte nicht geändert werden.')); }
    };

    const handleDownloadPdf = async (doc) => {
        setLoadingPdf(doc.id);
        try {
            const blob   = isInvoice ? await downloadInvoicePdf(doc.id) : await downloadOfferPdf(doc.id);
            const num    = isInvoice ? doc.invoice_number : doc.offer_number;
            const prefix = isInvoice ? 'Rechnung' : 'Angebot';
            downloadBlob(blob, `${prefix}_${(num || 'export').replace('/', '-')}.pdf`);
        } catch (err) { toast(extractApiError(err, 'PDF konnte nicht generiert werden.')); }
        finally { setLoadingPdf(null); }
    };

    // Same endpoint as the download; only the destination differs. A tab cannot send the
    // X-API-Key header, so the bytes go through a blob — see utils/pdfTab.js.
    const handlePreviewPdf = async (doc) => {
        const num    = isInvoice ? doc.invoice_number : doc.offer_number;
        const prefix = isInvoice ? 'Rechnung' : 'Angebot';
        const tab = openPendingTab(`${prefix} ${num || ''} wird erstellt…`.replace('  ', ' '));
        if (!tab) {
            toast('Der Browser hat das neue Tab blockiert. Bitte Pop-ups für diese Seite erlauben.');
            return;
        }
        setPreviewPdf(doc.id);
        try {
            const blob = isInvoice ? await downloadInvoicePdf(doc.id) : await downloadOfferPdf(doc.id);
            revealBlobInTab(tab, blob);
        } catch (err) {
            closePendingTab(tab);
            toast(extractApiError(err, 'PDF konnte nicht generiert werden.'));
        } finally {
            // Only clear the slot if it is still ours: with two previews in flight the
            // first to finish would otherwise re-enable the row that is still loading.
            setPreviewPdf(cur => (cur === doc.id ? null : cur));
        }
    };

    const statusOptions = isInvoice ? INVOICE_STATUSES : OFFER_STATUSES;

    const handleAddOrder = () => { setEditOrder(null); setConvertOffer(null); setOrderPrefill(null); setShowOrderModal(true); };
    const handleEditOrder = (order) => { setEditOrder(order); setConvertOffer(null); setOrderPrefill(null); setShowOrderModal(true); };
    const closeOrderModal = () => { setShowOrderModal(false); setConvertOffer(null); setOrderPrefill(null); };

    // Prefilled from the offer; everything except the project comes from it —
    // see utils/offerToOrder.js for why prices are dropped.
    const handleConvertOffer = (offer) => {
        setEditOrder(null);
        setConvertOffer(offer);
        setOrderPrefill(offerToOrderDraft(offer, orders));
        setShowOrderModal(true);
    };

    // The order number of an already converted offer, if that order is still around.
    const convertedOrderNumber = (orderId) =>
        orders.find(o => String(o.id) === String(orderId))?.order_number || null;

    const handleDeleteOrder = async (order) => {
        if (!await confirm(`Auftrag ${order.order_number} wirklich löschen?`, { confirmLabel: 'Löschen', danger: true })) return;
        try {
            await deleteOrder(order.id);
            if (filePreview?.order?.id === order.id) setFilePreview(null);
            load();
        } catch (err) { toast(extractApiError(err, 'Fehler beim Löschen.')); }
    };

    const handleSaveOrder = async (payload, file = null) => {
        try {
            let orderId = editOrder?.id;
            if (editOrder)          await updateOrder(editOrder.id, payload);
            // Converting also accepts the offer, so it is one request, not two.
            else if (convertOffer)  orderId = (await convertOfferToOrder(convertOffer.id, payload))?.order_id;
            else                    orderId = (await createOrder(payload))?.id;
            // A new order has no id until it exists, so its file is uploaded here.
            if (file && orderId) {
                try { await uploadOrderFile(orderId, file); }
                catch (err) {
                    toast(extractApiError(err, 'Auftrag gespeichert, Datei-Upload fehlgeschlagen.'));
                }
            }
            // Switch to the Aufträge tab so the converted offer's order is visible.
            if (convertOffer) setTab('orders');
            closeOrderModal();
            snapYearTo(payload.issue_date);
            load();
        } catch (err) { toast(extractApiError(err, 'Fehler beim Speichern.')); }
    };

    const handleShowOrderFile = async (order) => {
        if (filePreview?.order?.id === order.id) { setFilePreview(null); return; }
        setFileLoading(order.id);
        try {
            const blob = await getOrderFile(order.id);
            setFilePreview({ order, url: URL.createObjectURL(blob) });
        } catch (err) { toast(extractApiError(err, 'Datei konnte nicht geladen werden.')); }
        finally { setFileLoading(null); }
    };

    const handleQuickOrderStatus = async (orderId, newStatus) => {
        try {
            await updateOrder(orderId, { status: newStatus });
            load();
        } catch (err) { toast(extractApiError(err, 'Status konnte nicht geändert werden.')); }
    };

    return (
        <div className="invoices-page">

            {loadError && (
                <div className="alert alert--error" style={{ margin: '0 0 16px' }}>
                    {loadError}
                    <button className="alert__close" onClick={load} style={{ marginLeft: 12 }}>Erneut versuchen</button>
                </div>
            )}

            <div className="invoices-hero">
                <h2 className="invoices-hero-title">Finanzen</h2>
                <p className="invoices-hero-sub">
                    PDF-Erstellung mit eingebettetem ZUGFeRD 2.1 XML
                </p>
            </div>

            <div className="invoices-tabs">
                <button className={`invoices-tab${tab === 'overview'  ? ' active' : ''}`} onClick={() => setTab('overview')}>
                    Übersicht
                </button>
                {/* Counts follow the selected year, so a tab never claims 42 while its
                    table shows 12. */}
                <button className={`invoices-tab${tab === 'invoices'  ? ' active' : ''}`} onClick={() => setTab('invoices')}>
                    Rechnungen
                    {yearInvoices.length > 0 && <span style={{ marginLeft: 6, fontSize: '11px', opacity: 0.7 }}>({yearInvoices.length})</span>}
                </button>
                <button className={`invoices-tab${tab === 'offers' ? ' active' : ''}`} onClick={() => setTab('offers')}>
                    Angebote
                    {yearOffers.length > 0 && <span style={{ marginLeft: 6, fontSize: '11px', opacity: 0.7 }}>({yearOffers.length})</span>}
                </button>
                <button className={`invoices-tab${tab === 'orders' ? ' active' : ''}`} onClick={() => setTab('orders')}>
                    Aufträge
                    {yearOrders.length > 0 && <span style={{ marginLeft: 6, fontSize: '11px', opacity: 0.7 }}>({yearOrders.length})</span>}
                </button>
                <button className={`invoices-tab${tab === 'bwa' ? ' active' : ''}`} onClick={() => setTab('bwa')}>
                    BWA
                </button>

                {/* Not shown on BWA: that tab has a year of its own, restricted to the
                    months that were imported. */}
                {tab !== 'bwa' && (
                    <label className="invoices-year">
                        <span className="invoices-year-label">Jahr</span>
                        <select
                            className="inv-select invoices-year-select"
                            value={year}
                            aria-label="Jahr für die gesamte Finanzseite"
                            title="Gilt für Übersicht, Rechnungen, Angebote und Aufträge"
                            onChange={e => {
                                setYear(Number(e.target.value));
                                // The stored month belongs to the old year and would
                                // filter the list down to nothing.
                                setDocMonth('');
                            }}
                        >
                            {years.map(y => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </label>
                )}
            </div>

            {isOverview && (
                <InvoiceOverview
                    invoices={invoices}
                    year={year}
                    onStatusChange={(id, status) => handleQuickStatus(id, status, true)}
                    revenueTarget={revenueTargets[String(year)] ?? null}
                    invoicesTruncated={invoices.length >= INVOICE_LIMIT}
                />
            )}

            {isOrders && (
                <>
                    <div className="invoices-toolbar">
                        <div className="invoices-toolbar-left">
                            <span className="invoices-count">
                                {orderSearch ? `${visibleOrders.length} von ${yearOrders.length}` : yearOrders.length} {yearOrders.length === 1 ? 'Auftrag' : 'Aufträge'} in {year}
                            </span>
                            <input
                                className="invoices-search" type="search"
                                placeholder="Auftrag suchen…"
                                value={orderSearch}
                                onChange={e => setOrderSearch(e.target.value)}
                            />
                        </div>
                        <button className="inv-btn primary" onClick={handleAddOrder}>+ Neuer Auftrag</button>
                    </div>
                    {filePreview && (
                        <div className="ord-preview">
                            <div className="ord-preview-header">
                                <span className="ord-preview-title">
                                    Auftrag {filePreview.order.order_number}
                                    <span className="ord-preview-file"> · {filePreview.order.file_name}</span>
                                </span>
                                <div className="td-actions">
                                    <a
                                        className="inv-btn"
                                        href={filePreview.url}
                                        download={filePreview.order.file_name || 'auftrag'}
                                    >
                                        ⬇ Herunterladen
                                    </a>
                                    <button className="inv-btn" onClick={() => setFilePreview(null)}>
                                        Schließen
                                    </button>
                                </div>
                            </div>
                            <iframe
                                className="ord-preview-frame"
                                src={filePreview.url}
                                title={`Auftragsdatei ${filePreview.order.order_number}`}
                            />
                        </div>
                    )}

                    {yearOrders.length === 0 ? (
                        <div className="invoices-empty">
                            <div className="invoices-empty-icon">
                                <svg viewBox="0 0 48 48" fill="none">
                                    <rect x="8" y="4" width="32" height="40" rx="3" stroke="currentColor" strokeWidth="2"/>
                                    <path d="M16 16h16M16 22h12M16 28h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                </svg>
                            </div>
                            <p className="invoices-empty-title">Keine Aufträge in {year}</p>
                            <p className="invoices-empty-sub">Erstellen Sie Ihren ersten Auftrag über den Button oben rechts.</p>
                        </div>
                    ) : (
                        <div className="invoices-table-wrap">
                            <table className="invoices-table">
                                <thead>
                                    <tr>
                                        {[
                                            ['number', 'Auftragsnr.'], ['title', 'Titel'],
                                            ['project', 'Projekt'], ['company', 'Kunde'],
                                            ['issue_date', 'Datum'], ['period', 'Zeitraum'],
                                            ['status', 'Status'],
                                        ].map(([key, label]) => (
                                            <SortableTh key={key} sortKey={key} label={label}
                                                sort={orderSort} onSort={k => setOrderSort(p => nextSort(p, k))} />
                                        ))}
                                        <SortableTh sortKey="hours" label="Stunden" align="right"
                                            sort={orderSort} onSort={k => setOrderSort(p => nextSort(p, k))} />
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleOrders.length === 0 && (
                                        <tr><td colSpan={9} className="invoices-no-results">Keine Treffer für „{orderSearch}"</td></tr>
                                    )}
                                    {visibleOrders.map(order => (
                                        <tr key={order.id}>
                                            <td className="td-number">{order.order_number}</td>
                                            <td>{order.title || '—'}</td>
                                            <td>{order.project_name || '—'}</td>
                                            <td>{order.company_name || '—'}</td>
                                            <td>{fmtDate(order.issue_date)}</td>
                                            <td className="ord-period">
                                                {order.start_date || order.end_date
                                                    ? `${fmtDate(order.start_date)} – ${fmtDate(order.end_date)}`
                                                    : '—'}
                                            </td>
                                            <td>
                                                <select
                                                    className={`inv-status-select ${order.status}`}
                                                    value={order.status}
                                                    onChange={e => handleQuickOrderStatus(order.id, e.target.value)}
                                                >
                                                    {ORDER_STATUSES.map(s => (
                                                        <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="td-amount">{order.total_hours} h</td>
                                            <td>
                                                <div className="td-actions">
                                                    {order.file_name && (
                                                        <button
                                                            className={`inv-btn inv-btn--icon${filePreview?.order?.id === order.id ? ' primary' : ''}`}
                                                            onClick={() => handleShowOrderFile(order)}
                                                            disabled={fileLoading === order.id}
                                                            title={filePreview?.order?.id === order.id
                                                                ? 'Vorschau schließen'
                                                                : `Datei ansehen: ${order.file_name}`}
                                                            aria-label={`Datei ansehen: ${order.file_name}`}
                                                        >
                                                            {fileLoading === order.id
                                                                ? '…'
                                                                : filePreview?.order?.id === order.id ? '✕' : '👁'}
                                                        </button>
                                                    )}
                                                    <button className="inv-btn" onClick={() => handleEditOrder(order)}>
                                                        Bearbeiten
                                                    </button>
                                                    <button className="inv-btn danger" onClick={() => handleDeleteOrder(order)}>
                                                        Löschen
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}

            {!isOverview && !isOrders && tab !== 'bwa' && (
                <>
                    <div className="invoices-toolbar">
                        <div className="invoices-toolbar-left">
                            <span className="invoices-count">
                                {(docSearch || docMonth) ? `${visibleDocs.length} von ${docs.length}` : docs.length} {isInvoice
                                    ? (docs.length === 1 ? 'Rechnung' : 'Rechnungen')
                                    : (docs.length === 1 ? 'Angebot' : 'Angebote')} in {year}
                            </span>
                            <select
                                className="invoices-search invoices-month"
                                value={docMonth}
                                onChange={e => setDocMonth(e.target.value)}
                            >
                                <option value="">Alle Monate</option>
                                {docMonths.map(m => (
                                    <option key={m.value} value={m.value}>{m.label}</option>
                                ))}
                                {docMonth && !docMonths.some(m => m.value === docMonth) && (
                                    <option value={docMonth}>
                                        {`${MONTH_NAMES[Number(docMonth.slice(5, 7)) - 1]} ${docMonth.slice(0, 4)}`}
                                    </option>
                                )}
                            </select>
                            <input
                                className="invoices-search" type="search"
                                placeholder="Nr., Empfänger, Kommentar, Status…"
                                value={docSearch}
                                onChange={e => setDocSearch(e.target.value)}
                            />
                        </div>
                        <button className="inv-btn primary" onClick={handleAdd}>
                            + {isInvoice ? 'Neue Rechnung' : 'Neues Angebot'}
                        </button>
                    </div>

                    {docs.length === 0 ? (
                        <div className="invoices-empty">
                            <div className="invoices-empty-icon">
                                <svg viewBox="0 0 48 48" fill="none">
                                    <rect x="8" y="4" width="32" height="40" rx="3" stroke="currentColor" strokeWidth="2"/>
                                    <path d="M16 16h16M16 22h12M16 28h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                </svg>
                            </div>
                            <p className="invoices-empty-title">Keine {isInvoice ? 'Rechnungen' : 'Angebote'} in {year}</p>
                            <p className="invoices-empty-sub">
                                Erstellen Sie {isInvoice ? 'Ihre erste Rechnung' : 'Ihr erstes Angebot'} über den Button oben rechts.
                            </p>
                        </div>
                    ) : (
                        <div className="invoices-table-wrap">
                            <table className="invoices-table">
                                <thead>
                                    <tr>
                                        <SortableTh sortKey="number" label={isInvoice ? 'Rechnungsnr.' : 'Angebotsnr.'}
                                            sort={docSort} onSort={k => setDocSort(p => nextSort(p, k))} />
                                        <SortableTh sortKey="recipient" label="Empfänger"
                                            sort={docSort} onSort={k => setDocSort(p => nextSort(p, k))} />
                                        <SortableTh sortKey="comment" label="Kommentar"
                                            sort={docSort} onSort={k => setDocSort(p => nextSort(p, k))} />
                                        <SortableTh sortKey="issue_date" label="Datum"
                                            sort={docSort} onSort={k => setDocSort(p => nextSort(p, k))} />
                                        <SortableTh sortKey="due" label={isInvoice ? 'Fällig' : 'Gültig bis'}
                                            sort={docSort} onSort={k => setDocSort(p => nextSort(p, k))} />
                                        <SortableTh sortKey="status" label="Status"
                                            sort={docSort} onSort={k => setDocSort(p => nextSort(p, k))} />
                                        <SortableTh sortKey="net" label="Netto" align="right"
                                            sort={docSort} onSort={k => setDocSort(p => nextSort(p, k))} />
                                        <SortableTh sortKey="gross" label="Brutto" align="right"
                                            sort={docSort} onSort={k => setDocSort(p => nextSort(p, k))} />
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleDocs.length === 0 && (
                                        <tr><td colSpan={9} className="invoices-no-results">Keine Treffer für „{docSearch}"</td></tr>
                                    )}
                                    {visibleDocs.map(doc => {
                                        const num   = isInvoice ? doc.invoice_number : doc.offer_number;
                                        const dateB = isInvoice ? doc.due_date       : doc.valid_until;
                                        return (
                                            <tr key={doc.id}>
                                                <td className="td-number">{num}</td>
                                                <td>{doc.recipient_name}</td>
                                                <td className="td-comment" title={doc.internal_note || ''}>
                                                    {doc.internal_note || '—'}
                                                </td>
                                                <td>{fmtDate(doc.issue_date)}</td>
                                                <td>{fmtDate(dateB)}</td>
                                                <td>
                                                    <select
                                                        className={`inv-status-select ${doc.status}`}
                                                        value={doc.status}
                                                        onChange={e => handleQuickStatus(doc.id, e.target.value, isInvoice)}
                                                    >
                                                        {statusOptions.map(s => (
                                                            <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>
                                                        ))}
                                                    </select>
                                                    {doc.status === 'rejected' && doc.rejection_reason && (
                                                        <div className="inv-reject-reason" title={doc.rejection_reason}>
                                                            {doc.rejection_reason}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="td-amount">
                                                    {fmtAmount(doc.total_net, doc.currency)}
                                                    {doc.currency !== 'EUR' && <span className="td-eur-hint">{fmtEur(eurAmount(doc.total_net, doc))}</span>}
                                                </td>
                                                <td className="td-amount td-amount--muted">
                                                    {fmtAmount(doc.total_gross, doc.currency)}
                                                    {doc.currency !== 'EUR' && (
                                                        <span
                                                            className={`td-eur-hint${hasFxCorrection(doc) ? ' td-eur-hint--fx' : ''}`}
                                                            title={hasFxCorrection(doc)
                                                                ? `Bankgutschrift ${fmtEur(doc.settled_amount_eur)}`
                                                                  + ` · Kursdifferenz ${fmtSigned(fxDifference(doc))}`
                                                                  + (doc.settled_on ? ` · ${fmtDate(doc.settled_on)}` : '')
                                                                : undefined}
                                                        >
                                                            {fmtEur(eurAmount(doc.total_gross, doc))}
                                                            {hasFxCorrection(doc) && ` (${fmtSigned(fxDifference(doc))})`}
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    <div className="td-actions">
                                                        {/* Icon-only: the actions cell is already four buttons wide
                                                            on an accepted offer. */}
                                                        <button
                                                            className="inv-btn inv-btn--icon"
                                                            onClick={() => handlePreviewPdf(doc)}
                                                            disabled={previewPdf === doc.id}
                                                            title={`Vorschau in neuem Tab: ${isInvoice ? 'Rechnung' : 'Angebot'} ${num}`}
                                                            aria-label={`Vorschau in neuem Tab: ${isInvoice ? 'Rechnung' : 'Angebot'} ${num}`}
                                                        >
                                                            {previewPdf === doc.id ? '…' : '👁'}
                                                        </button>
                                                        <button
                                                            className="inv-btn primary"
                                                            onClick={() => handleDownloadPdf(doc)}
                                                            disabled={loadingPdf === doc.id}
                                                        >
                                                            {loadingPdf === doc.id ? '…' : '⬇ PDF'}
                                                        </button>
                                                        {/* Angebot → Auftrag, only on an accepted offer: the row has
                                                            no width to spare for a button nobody can use yet. */}
                                                        {!isInvoice && (doc.converted_order_id ? (
                                                            <span className="inv-converted-hint" title="Dieses Angebot wurde bereits in einen Auftrag überführt.">
                                                                → {convertedOrderNumber(doc.converted_order_id) || 'Auftrag'}
                                                            </span>
                                                        ) : doc.status === 'accepted' && (
                                                            <button
                                                                className="inv-btn inv-btn--icon"
                                                                onClick={() => handleConvertOffer(doc)}
                                                                title={`Auftrag aus Angebot ${num} erstellen`}
                                                            >
                                                                → Auftrag
                                                            </button>
                                                        ))}
                                                        <button className="inv-btn" onClick={() => handleEdit(doc)}>
                                                            Bearbeiten
                                                        </button>
                                                        <button
                                                            className="inv-btn inv-btn--icon"
                                                            onClick={() => handleDuplicate(doc)}
                                                            title={`${isInvoice ? 'Rechnung' : 'Angebot'} ${num} duplizieren`}
                                                            aria-label={`${isInvoice ? 'Rechnung' : 'Angebot'} ${num} duplizieren`}
                                                        >
                                                            ⧉
                                                        </button>
                                                        <button className="inv-btn danger" onClick={() => handleDelete(doc)}>
                                                            Löschen
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}

            {tab === 'bwa' && (
                <>
                    <p style={{ color: 'var(--color-text-secondary)', margin: '0.75rem 0 0.25rem', fontSize: '0.875rem' }}>
                        Die Betriebswirtschaftliche Auswertung (BWA) gibt einen Überblick über Erträge, Kosten und Ergebnis — ergänzend zu den Rechnungsdaten.
                    </p>
                    <div style={{ display: 'flex', gap: '0.25rem', background: 'var(--color-surface, #f1f5f9)', borderRadius: 8, padding: '3px', width: 'fit-content', margin: '1rem 0 0.5rem' }}>
                        {[{ key: 'entry', label: 'Eingabe' }, { key: 'analytics', label: 'Auswertung' }].map(t => (
                            <button
                                key={t.key}
                                onClick={() => setBwaTab(t.key)}
                                style={{
                                    padding: '0.3rem 1rem',
                                    borderRadius: 6,
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                    fontWeight: bwaTab === t.key ? 600 : 400,
                                    background: bwaTab === t.key ? 'var(--color-primary, #3b82f6)' : 'transparent',
                                    color: bwaTab === t.key ? '#fff' : 'var(--color-text-muted, #64748b)',
                                    transition: 'background 0.15s, color 0.15s',
                                }}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                    {bwaTab === 'entry'     && <BwaDashboard />}
                    {bwaTab === 'analytics' && <BwaAnalytics />}
                </>
            )}

            {showModal && (
                <InvoiceModal
                    isInvoice={isInvoice}
                    doc={editDoc}
                    template={editDoc ? null : template}
                    allDocs={allDocs}
                    onClose={() => setShowModal(false)}
                    onSave={handleSave}
                />
            )}

            {showOrderModal && (
                <OrderModal
                    order={editOrder}
                    allOrders={orders}
                    prefill={orderPrefill}
                    onClose={closeOrderModal}
                    onSave={handleSaveOrder}
                />
            )}
        </div>
    );
}
